import request from "superagent";

declare var ApiRouteBase: string;

const apiRoot = ApiRouteBase.replace(/\/$/, "");
const qbBasePath = `${apiRoot}/Resources/QbPayments`;

export type CardInput = {
    number: string;
    expMonth: number;
    expYear: number;
    cvc: string;
    name: string;
    postalCode: string;
};

export type SavedCard = {
    cardId: string;
    brand?: string;
    last4?: string;
    expMonth?: string | number;
    expYear?: string | number;
    displayLabel: string;
    customerId: string;
    isDefault?: boolean;
    /** From GET saved-cards / save response; "Credit" | "Debit" | "Prepaid" or omitted. */
    cardFundingType?: string | null;
};

export type ChargeResult = {
    intuitChargeId?: string;
    paymentId?: string;
    customerId?: string;
    /** Populated for ACH responses; 24-digit Intuit on-file id. */
    bankAccountId?: string;
    /** TGCC EstimatePayment id; for ACH the row is created with ChargeStatus = Pending. */
    estimatePaymentId?: number;
    /** Which rail produced this charge — used by the UI to choose card vs ACH messaging. */
    paymentRail?: "card" | "ach";
    /** True for ACH charges (settlement is pending; backend marks payment Pending). */
    isPending?: boolean;
    raw: unknown;
};

export type DevicePayload = {
    id?: string;
    type?: string;
    ipAddress?: string;
    phoneNumber?: string;
};

export type ApiError = Error & {
    status?: number;
    body?: any;
};

type SavedCardsBackendResponse = {
    qbCustomerId: string;
    customerEmail?: string;
    cards: any[];
};

export type SavedBankAccount = {
    bankAccountId: string;
    bankName?: string;
    accountType: "checking" | "savings";
    last4: string;
    customerId: string;
    displayLabel: string;
    isDefault?: boolean;
};

type WebOrderAuth = {
    webOrderToken?: string;
    webOrderNumber?: string;
};

type SaveCardResponse = {
    cardId?: string;
    card?: any;
};

type SaveBankResponse = {
    bankAccountId?: string;
    bankAccount?: any;
};

type ChargeSavedCardInput = {
    cardId: string;
    amount: number;
    currency: string;
    description: string;
    customerId: string;
    cardFundingType?: string;
};

type ChargeTokenInput = {
    token: string;
    amount: number;
    currency: string;
    description: string;
    customerId: string;
    cardFundingType?: string;
};

type ChargeSavedBankInput = {
    bankId: string;
    amount: number;
    currency: string;
    description: string;
    customerId: string;
    /** Defaults to "WEB" on the backend if omitted. */
    paymentMode?: string;
    checkNumber?: string | null;
    device?: DevicePayload;
};

/**
 * One-time ACH (new bank) charge. The backend internally tokenizes -> bank-on-file
 * -> /payments/echecks, so a paymentsToken is REQUIRED and the resulting bank is
 * always persisted in CustomerPaymentMethods.
 */
type ChargeOneTimeBankInput = {
    paymentsToken: string;
    amount: number;
    currency: string;
    description: string;
    customerId: string;
    accountHolderName?: string;
    paymentMode?: string;
    checkNumber?: string | null;
    device?: DevicePayload;
};

type SaveCardInput = {
    customerId: string;
    cardholderName: string;
    // Must be the browser-side Intuit token (PAN/CVC must not be sent to TGCC).
    paymentsToken: string;
    cardFundingType?: string;
};

type BankInput = {
    name: string;
    routingNumber: string;
    accountNumber: string;
    accountType?: "PERSONAL_CHECKING" | "PERSONAL_SAVINGS" | string;
};

type SaveBankInput = {
    /** Bank token from the Intuit /quickbooks/v4/payments/tokens widget. */
    paymentsToken: string;
    accountHolderName?: string;
    /**
     * Caller-side context only — `customerId` is no longer part of the HTTP
     * body for /intuit-save-bank-on-file (the backend resolves it from the
     * web-order JWT). Kept here so the returned SavedBankAccount carries the
     * id the rest of the UI expects.
     */
    customerId?: string;
    /** Optional checking/savings hint — used only for the UI label fallback. */
    accountTypeHint?: "checking" | "savings";
    /** Optional last-4 hint for the UI label until /saved-cards is refreshed. */
    last4Hint?: string;
    /**
     * Raw bank fields are rejected by the backend with HTTP 403 to keep us out
     * of PCI / NACHA scope. We trip the local guard before the request leaves
     * the browser so devs see a clear error.
     */
    bank?: BankInput;
};

function blockRawSensitiveDataInProduction(reason: string): never {
    const err = new Error(
        `${reason} Raw payment data is blocked in this flow. Use browser-side Intuit tokenization and send tokenized data only.`
    ) as ApiError;
    err.status = 400;
    throw err;
}

type IntuitPaymentsDeployment = "SANDBOX" | "PRODUCTION";

let cachedIntuitPaymentsDeployment: IntuitPaymentsDeployment | null = null;
let intuitPaymentsDeploymentPromise: Promise<IntuitPaymentsDeployment> | null = null;

function deploymentFromWindow(): IntuitPaymentsDeployment {
    const deployment = (window as any)?.paymentDeployment;
    return deployment === "SANDBOX" ? "SANDBOX" : "PRODUCTION";
}

function toIntuitPaymentsBaseUrl(deployment: IntuitPaymentsDeployment): string {
    return deployment === "SANDBOX"
        ? "https://sandbox.api.intuit.com"
        : "https://api.intuit.com";
}

async function resolveIntuitPaymentsDeployment(): Promise<IntuitPaymentsDeployment> {
    if (cachedIntuitPaymentsDeployment) {
        return cachedIntuitPaymentsDeployment;
    }

    if (!intuitPaymentsDeploymentPromise) {
        intuitPaymentsDeploymentPromise = fetch(`${qbBasePath}/intuit-payments-deployment`, {
            method: "GET",
            credentials: "omit",
        })
            .then(async (res) => {
                if (!res.ok) {
                    throw new Error(`Failed to resolve Intuit payments deployment (${res.status}).`);
                }
                const body = await res.json();
                const deployment = body?.deployment === "SANDBOX" ? "SANDBOX" : "PRODUCTION";
                cachedIntuitPaymentsDeployment = deployment;
                return deployment;
            })
            .catch(() => {
                // Fallback for local dev before the API is available.
                cachedIntuitPaymentsDeployment = deploymentFromWindow();
                return cachedIntuitPaymentsDeployment;
            });
    }

    return intuitPaymentsDeploymentPromise;
}

async function getIntuitPaymentsBaseUrl(): Promise<string> {
    // Must match TGCCService UsesQuickBooksSandbox() host selection.
    // Tokens are environment-scoped — a production token will fail sandbox createFromToken
    // with PMT-4000 "token is invalid / not associated with payment data".
    const deployment = await resolveIntuitPaymentsDeployment();
    return toIntuitPaymentsBaseUrl(deployment);
}

function buildRequestId(): string {
    try {
        if (window.crypto && typeof window.crypto.getRandomValues === "function") {
            const bytes = new Uint8Array(16);
            window.crypto.getRandomValues(bytes);
            const hex = Array.prototype.map.call(bytes, function (b: number) {
                return ("0" + b.toString(16)).slice(-2);
            }).join("");
            return "tgcc-" + hex;
        }
    } catch {
        // Fall back to Math.random below.
    }
    return "tgcc-" + Math.random().toString(16).slice(2, 18);
}

async function tokenizeWithIntuit(payload: any): Promise<string> {
    const requestId = buildRequestId();
    const baseUrl = await getIntuitPaymentsBaseUrl();
    const res = await fetch(baseUrl + "/quickbooks/v4/payments/tokens", {
        method: "POST",
        headers: {
            "Content-Type": "application/json",
            "Request-Id": requestId,
        },
        body: JSON.stringify(payload),
        credentials: "omit",
    });

    const text = await res.text();
    let body: any = {};
    try {
        body = text ? JSON.parse(text) : {};
    } catch (e) {
        body = { raw: text };
    }

    if (!res.ok) {
        let apiMsg = "";
        if (body && body.errors && body.errors.length && body.errors[0].message) {
            apiMsg = body.errors[0].message;
        } else if (body && body.message) {
            apiMsg = body.message;
        } else {
            apiMsg = "Tokenization failed.";
        }
        throw new Error(apiMsg);
    }

    const token = (body && (body.value || body.token || body.paymentsToken)) || "";
    if (!token) {
        throw new Error("Intuit tokenization succeeded but no token was returned.");
    }
    return token;
}

function buildApiError(err: any): ApiError {
    const responseBody = err?.response?.body;
    const fallbackMessage = err?.message || "Request failed";
    const msg = responseBody?.message || responseBody?.Message || fallbackMessage;
    const wrapped = new Error(msg) as ApiError;
    wrapped.status = err?.status || err?.response?.status;
    wrapped.body = responseBody || err?.response?.text || null;
    return wrapped;
}

function applyWebOrderBearer(req: request.Request, bearerToken?: string | null): request.Request {
    const t = typeof bearerToken === "string" ? bearerToken.trim() : "";
    if (!t) return req;
    return req.set("Authorization", `Bearer ${t}`);
}

async function postJson<TResponse>(path: string, payload: any, webOrderJwt?: string | null): Promise<TResponse> {
    try {
        const req = applyWebOrderBearer(request.post(`${qbBasePath}${path}`), webOrderJwt).send(payload);
        const res = await req;
        return (res.body || {}) as TResponse;
    } catch (err) {
        throw buildApiError(err);
    }
}

async function getJson<TResponse>(
    path: string,
    query: Record<string, string>,
    webOrderJwt?: string | null
): Promise<TResponse> {
    try {
        const req = applyWebOrderBearer(request.get(`${qbBasePath}${path}`).query(query), webOrderJwt);
        const res = await req;
        return (res.body || {}) as TResponse;
    } catch (err) {
        throw buildApiError(err);
    }
}

export async function tokenizeCardInBrowser(cardInput: CardInput): Promise<string> {
    const expMonthRaw = cardInput && cardInput.expMonth != null ? String(cardInput.expMonth) : "";
    const expMonth = expMonthRaw ? expMonthRaw.padStart(2, "0") : "";

    const payload = {
        card: {
            name: cardInput && cardInput.name ? String(cardInput.name) : "",
            number: cardInput && cardInput.number ? String(cardInput.number).replace(/\s+/g, "") : "",
            expMonth: expMonth,
            expYear: cardInput && cardInput.expYear != null ? String(cardInput.expYear) : "",
            cvc: cardInput && cardInput.cvc ? String(cardInput.cvc) : "",
            address: {
                postalCode: cardInput && cardInput.postalCode ? String(cardInput.postalCode) : "",
            },
        },
    };

    return tokenizeWithIntuit(payload);
}

export async function tokenizeBankInBrowser(bankInput: BankInput): Promise<string> {
    const payload = {
        bankAccount: {
            name: bankInput && bankInput.name ? String(bankInput.name) : "",
            routingNumber: bankInput && bankInput.routingNumber ? String(bankInput.routingNumber) : "",
            accountNumber: bankInput && bankInput.accountNumber ? String(bankInput.accountNumber) : "",
            accountType: bankInput && bankInput.accountType ? String(bankInput.accountType) : "PERSONAL_CHECKING",
        },
    };

    return tokenizeWithIntuit(payload);
}

function normalizeSavedCard(cardId: string, customerId: string, card?: any): SavedCard {
    const brand = card?.brand || card?.cardType || card?.type || undefined;
    const last4 = card?.last4 || card?.lastFour || (typeof card?.number === "string" ? card.number.slice(-4) : undefined);
    const expMonth = card?.expMonth;
    const expYear = card?.expYear;
    const safeBrand = brand || "Card";
    const safeLast4 = last4 || "••••";
    const mm = expMonth ? String(expMonth).padStart(2, "0") : "MM";
    const yyyy = expYear ? String(expYear) : "YYYY";
    const cardFundingType = card?.cardFundingType ?? card?.card_funding_type ?? undefined;
    return {
        cardId,
        brand,
        last4,
        expMonth,
        expYear,
        displayLabel: `${safeBrand} ••••${safeLast4} exp ${mm}/${yyyy}`,
        customerId,
        ...(cardFundingType != null && cardFundingType !== "" ? { cardFundingType: String(cardFundingType) } : {}),
    };
}

function normalizeBackendCardToSavedCard(card: any, customerId: string): SavedCard {
    const cardId: string =
        String(card?.cardId || card?.id || card?.cardID || card?.card_id || "");
    const brand = card?.cardBrand || card?.brand || card?.cardType || card?.type || undefined;
    const last4 = card?.last4 || card?.lastFour || card?.last_four || undefined;
    const expMonth = card?.expMonth || card?.exp_month || undefined;
    const expYear = card?.expYear || card?.exp_year || undefined;
    const displayLabelFromBackend = card?.displayLabel ? String(card.displayLabel) : undefined;

    // Keep backend displayLabel (usually cardholder name) and let UI compose the full line.
    const cardFundingType = card?.cardFundingType ?? card?.card_funding_type ?? null;
    return {
        cardId,
        brand,
        last4,
        expMonth,
        expYear,
        displayLabel: displayLabelFromBackend || "Saved card",
        customerId,
        isDefault: Boolean(card?.isDefault),
        ...(cardFundingType != null && String(cardFundingType) !== ""
            ? { cardFundingType: String(cardFundingType) }
            : {}),
    };
}

function normalizeBackendBankToSavedBankAccount(bank: any, customerId: string): SavedBankAccount {
    // GET /saved-cards returns BOTH cards and banks; pass the 24-digit
    // `intuitPaymentMethodId` back to /intuit-charge-saved-bank as `bankId`.
    const bankAccountId = String(bank?.intuitPaymentMethodId || bank?.id || "");
    const accountTypeRaw = String(bank?.cardBrand || bank?.accountType || "").toUpperCase();
    const accountType: "checking" | "savings" = accountTypeRaw.includes("SAVINGS") ? "savings" : "checking";
    const last4 = String(bank?.last4 || "0000");
    const backendLabel = bank?.displayLabel ? String(bank.displayLabel).trim() : "";
    const accountTypeLabel = accountType.toUpperCase();
    const composedLabel = backendLabel
        ? `${accountTypeLabel} ••••${last4} — ${backendLabel}`
        : `${accountTypeLabel} ••••${last4}`;
    return {
        bankAccountId,
        bankName: backendLabel || "Bank Account",
        accountType,
        last4,
        customerId,
        displayLabel: composedLabel,
        isDefault: Boolean(bank?.isDefault),
    };
}

export async function fetchSavedPaymentMethods(
    webOrderToken: string
): Promise<{ qbCustomerId: string; cards: SavedCard[]; accounts: SavedBankAccount[] }> {
    const res = await getJson<SavedCardsBackendResponse>("/saved-cards", { webOrderToken }, webOrderToken);
    const qbCustomerId = String(res?.qbCustomerId || "");
    const methods = Array.isArray(res?.cards) ? res.cards : [];
    const cards = methods
        .filter((c: any) => String(c?.methodType || "").toLowerCase() !== "bank")
        .map((c: any) => normalizeBackendCardToSavedCard(c, qbCustomerId));
    const accounts = Array.isArray(res?.cards)
        ? res.cards
              .filter((c: any) => String(c?.methodType || "").toLowerCase() === "bank")
              .map((c: any) => normalizeBackendBankToSavedBankAccount(c, qbCustomerId))
        : [];
    return { qbCustomerId, cards, accounts };
}

export async function saveCardOnFile(input: SaveCardInput, webOrderAuth?: WebOrderAuth): Promise<SavedCard> {
    const tokenValue = typeof input.paymentsToken === "string" ? input.paymentsToken.trim() : "";
    if (!tokenValue) {
        const err = new Error("saveCardOnFile requires a payments token.") as ApiError;
        throw err;
    }

    const payload: Record<string, unknown> = {
        customerId: input.customerId,
        cardholderName: input.cardholderName,
        paymentsToken: tokenValue,
        webOrderToken: webOrderAuth?.webOrderToken,
        webOrderNumber: webOrderAuth?.webOrderNumber,
    };
    if (input.cardFundingType) {
        payload.cardFundingType = input.cardFundingType;
    }

    const res = await postJson<SaveCardResponse>("/intuit-save-card-on-file", payload, webOrderAuth?.webOrderToken);
    const cardId = res.cardId || res.card?.id || "";
    if (!cardId) {
        const err = new Error("Card was saved but no cardId was returned.") as ApiError;
        err.body = res;
        throw err;
    }
    return normalizeSavedCard(cardId, input.customerId, res.card);
}

/**
 * Save-only bank-on-file path. Targets `POST /intuit-save-bank-on-file` and
 * persists the bank in CustomerPaymentMethods without debiting it.
 *
 * The backend rejects raw bank fields (HTTP 403) and rejects missing tokens
 * (HTTP 400). HTTP 409 means Intuit already had this bank on file for the
 * customer — the response carries `existingBankAccountId`, which we treat
 * as success and surface to the caller so the UI can select it.
 */
export async function saveBankOnFile(input: SaveBankInput, webOrderAuth?: WebOrderAuth): Promise<SavedBankAccount> {
    if (input.bank) {
        blockRawSensitiveDataInProduction("saveBankOnFile() cannot include raw bank data.");
    }
    const tokenValue = typeof input.paymentsToken === "string" ? input.paymentsToken.trim() : "";
    if (!tokenValue) {
        const err = new Error("saveBankOnFile requires a payments token.") as ApiError;
        err.status = 400;
        throw err;
    }

    const payload: Record<string, unknown> = {
        paymentsToken: tokenValue,
    };
    if (input.accountHolderName) payload.accountHolderName = input.accountHolderName;

    let res: SaveBankResponse;
    try {
        res = await postJson<SaveBankResponse>("/intuit-save-bank-on-file", payload, webOrderAuth?.webOrderToken);
    } catch (err) {
        const apiErr = err as ApiError;
        // 409 (or any 4xx that carries an existingBankAccountId) means the bank
        // is already on file. Reuse it instead of failing the UX.
        const existingId = String((apiErr?.body as any)?.existingBankAccountId || "");
        if (apiErr?.status === 409 && existingId) {
            return buildSavedBankAccount(existingId, input);
        }
        if (existingId) {
            return buildSavedBankAccount(existingId, input);
        }
        throw err;
    }

    const bankAccountId = String(res?.bankAccountId || res?.bankAccount?.id || "");
    if (!bankAccountId) {
        const err = new Error("Bank account was saved but no bankAccountId was returned.") as ApiError;
        err.body = res;
        throw err;
    }
    return buildSavedBankAccount(bankAccountId, input, res?.bankAccount);
}

function buildSavedBankAccount(
    bankAccountId: string,
    input: SaveBankInput,
    bankFromRes?: any
): SavedBankAccount {
    const accountTypeRaw = String(
        bankFromRes?.cardBrand || bankFromRes?.accountType || input.accountTypeHint || ""
    ).toUpperCase();
    const accountType: "checking" | "savings" = accountTypeRaw.includes("SAVINGS") ? "savings" : "checking";
    const acctNumFromRes = String(bankFromRes?.accountNumber || "");
    const last4 = (acctNumFromRes.slice(-4) || input.last4Hint || "").replace(/[^\d]/g, "") || "0000";
    const holder = input.accountHolderName ? String(input.accountHolderName).trim() : "";
    const labelBase = `${accountType.toUpperCase()} ••••${last4}`;
    return {
        bankAccountId,
        bankName: "Bank Account",
        accountType,
        last4,
        customerId: input.customerId || "",
        displayLabel: holder ? `${labelBase} — ${holder}` : labelBase,
    };
}

function normalizeChargeStatus(rawStatus: unknown): string {
    return String(rawStatus || "")
        .trim()
        .replace(/_/g, "")
        .toUpperCase();
}

const FAILED_CHARGE_STATUSES = new Set([
    "FAILED",
    "DECLINED",
    "CANCELLED",
    "CANCELED",
    "VOIDED",
    "RETURNED",
    "REJECTED",
]);

/**
 * Defense in depth: Intuit (or older backends) may return HTTP 2xx with status DECLINED/FAILED.
 * Reject before callers approve the estimate or show "Payment successful".
 */
function assertChargeNotFailed(raw: any, rail?: "card" | "ach"): void {
    const status = normalizeChargeStatus(raw?.paymentsCharge?.status ?? raw?.status);
    if (!status || !FAILED_CHARGE_STATUSES.has(status)) return;

    const isDeclined = status === "DECLINED";
    const message = isDeclined
        ? rail === "ach"
            ? "Your bank payment was declined. Please update your payment information and try again."
            : "Your card was declined. Please update your card information and try again."
        : rail === "ach"
          ? "Your bank payment failed. Please update your payment information and try again."
          : "Payment failed. Please update your card information and try again.";

    const err = new Error(message) as ApiError;
    err.status = 400;
    // Omit body.message — getPaymentErrorText would otherwise duplicate error.message.
    err.body = {
        paymentsCharge: raw?.paymentsCharge,
    };
    throw err;
}

/** Returns a user-facing decline/failure message when charge status is failed; otherwise null. */
export function getFailedChargeUserMessage(charge: ChargeResult | null | undefined): string | null {
    if (!charge) return null;
    try {
        assertChargeNotFailed(charge.raw, charge.paymentRail);
        return null;
    } catch (err) {
        return String((err as Error)?.message || "Payment failed. Please update your payment information and try again.");
    }
}

function normalizeChargeResult(raw: any, rail?: "card" | "ach"): ChargeResult {
    assertChargeNotFailed(raw, rail);

    const result: ChargeResult = {
        intuitChargeId: raw?.paymentsCharge?.id || raw?.chargeId,
        paymentId: raw?.paymentId,
        customerId: raw?.customerId,
        raw,
    };
    if (rail) {
        result.paymentRail = rail;
        if (rail === "ach") result.isPending = true;
    }
    const bankAccountId = raw?.bankAccountId;
    if (bankAccountId != null && String(bankAccountId) !== "") {
        result.bankAccountId = String(bankAccountId);
    }
    const estPaymentId = raw?.estimatePaymentId;
    if (estPaymentId != null && estPaymentId !== "") {
        const asNumber = Number(estPaymentId);
        if (!Number.isNaN(asNumber)) result.estimatePaymentId = asNumber;
    }
    return result;
}

export async function chargeSavedCard(input: ChargeSavedCardInput, webOrderAuth?: WebOrderAuth): Promise<ChargeResult> {
    const payload: Record<string, unknown> = {
        cardId: input.cardId,
        amount: input.amount,
        currency: input.currency,
        description: input.description,
        customerId: input.customerId,
        webOrderToken: webOrderAuth?.webOrderToken,
        webOrderNumber: webOrderAuth?.webOrderNumber,
    };
    if (input.cardFundingType) {
        payload.cardFundingType = input.cardFundingType;
    }
    const res = await postJson<any>("/intuit-charge-saved-card", payload, webOrderAuth?.webOrderToken);
    return normalizeChargeResult(res, "card");
}

export async function chargeOneTimeToken(input: ChargeTokenInput, webOrderAuth?: WebOrderAuth): Promise<ChargeResult> {
    const payload: Record<string, unknown> = {
        paymentsToken: input.token,
        amount: input.amount,
        currency: input.currency,
        description: input.description,
        customerId: input.customerId,
        webOrderToken: webOrderAuth?.webOrderToken,
        webOrderNumber: webOrderAuth?.webOrderNumber,
    };
    if (input.cardFundingType) {
        payload.cardFundingType = input.cardFundingType;
    }
    const res = await postJson<any>("/intuit-charge", payload, webOrderAuth?.webOrderToken);
    return normalizeChargeResult(res, "card");
}

/**
 * One-time ACH charge for a brand-new bank account.
 *
 * Targets `POST /Resources/QbPayments/intuit-charge-bank`. The backend
 * tokenizes the bank with Intuit, persists it in CustomerPaymentMethods,
 * and debits via /payments/echecks. ACH does not capture immediately;
 * callers should treat the response as "submitted, pending settlement".
 */
export async function chargeOneTimeBank(
    input: ChargeOneTimeBankInput,
    webOrderAuth?: WebOrderAuth
): Promise<ChargeResult> {
    const token = typeof input.paymentsToken === "string" ? input.paymentsToken.trim() : "";
    if (!token) {
        const err = new Error("chargeOneTimeBank requires a payments token.") as ApiError;
        err.status = 400;
        throw err;
    }

    const payload: Record<string, unknown> = {
        paymentsToken: token,
        amount: input.amount,
        currency: input.currency,
        description: input.description,
        customerId: input.customerId,
        webOrderToken: webOrderAuth?.webOrderToken,
        webOrderNumber: webOrderAuth?.webOrderNumber,
    };
    if (input.accountHolderName) payload.accountHolderName = input.accountHolderName;
    if (input.paymentMode) payload.paymentMode = input.paymentMode;
    if (input.checkNumber !== undefined) payload.checkNumber = input.checkNumber;
    if (input.device) payload.device = input.device;

    const res = await postJson<any>("/intuit-charge-bank", payload, webOrderAuth?.webOrderToken);
    return normalizeChargeResult(res, "ach");
}

export async function chargeSavedBank(input: ChargeSavedBankInput, webOrderAuth?: WebOrderAuth): Promise<ChargeResult> {
    // Whitelist fields explicitly: /intuit-charge-saved-bank now rejects paymentsToken,
    // so we never want to forward arbitrary caller-supplied keys.
    const payload: Record<string, unknown> = {
        bankId: input.bankId,
        amount: input.amount,
        currency: input.currency,
        description: input.description,
        customerId: input.customerId,
        webOrderToken: webOrderAuth?.webOrderToken,
        webOrderNumber: webOrderAuth?.webOrderNumber,
    };
    if (input.paymentMode) payload.paymentMode = input.paymentMode;
    if (input.checkNumber !== undefined) payload.checkNumber = input.checkNumber;
    if (input.device) payload.device = input.device;
    const res = await postJson<any>("/intuit-charge-saved-bank", payload, webOrderAuth?.webOrderToken);
    return normalizeChargeResult(res, "ach");
}
